import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SITE } from "@/config/site";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Your message has been received.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40"
      style={{ backgroundColor: "#EBE8E0" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="overline mb-6">09 — Reach out</p>
          <h2
            className="serif text-4xl md:text-5xl text-[#15243D] leading-[1.1]"
            style={{ fontWeight: 300 }}
          >
            A note,<br />
            a <em className="italic text-[#C07865]">question</em>,<br />
            a beginning.
          </h2>
          <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-[#5A5D63] max-w-md">
            For general questions, partnerships, or to simply say hello. For
            coaching, please use the application form — it lets me understand
            where you are.
          </p>
          <div className="mt-10 pt-10 border-t border-[#15243D]/15 text-sm font-light text-[#5A5D63] space-y-2">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A8D93]">
              Direct
            </p>
            <a
              href={`mailto:${SITE.contactEmail}`}
              className="text-[#15243D] hover:text-[#C07865] transition-colors"
              data-testid="contact-direct-email"
            >
              {SITE.contactEmail}
            </a>
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {!sent ? (
            <form
              onSubmit={submit}
              className="space-y-8"
              data-testid="contact-form"
            >
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  className="anchor-input"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  className="anchor-input"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  className="anchor-input"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="contact-submit-button"
              >
                {loading ? "Sending…" : "Send message"}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
            </form>
          ) : (
            <div
              className="border-l-2 border-[#C07865] pl-6 py-2"
              data-testid="contact-success"
            >
              <p
                className="serif text-2xl text-[#15243D] mb-3"
                style={{ fontWeight: 300 }}
              >
                Received with care.
              </p>
              <p className="text-base font-light text-[#5A5D63]">
                Thank you for reaching out. I read every message personally and
                will reply within a few days.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 text-xs tracking-[0.2em] uppercase text-[#C07865] hover:text-[#A66352] transition-colors"
                data-testid="contact-send-another"
              >
                Send another →
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
